import { lastView as emptyLastView } from '@baseValues'
import { toVector3 } from '@utils/conversion/toVector3'
import type CameraControls from 'camera-controls'
import { Vector3 } from 'three'

export type CameraView = {
  position: Vector3
  target: Vector3
}

export type PersistedCameraView = {
  position: [number, number, number]
  target: [number, number, number]
}

type CameraViewLike = {
  position?: Vector3 | { x: number; y: number; z: number } | [number, number, number]
  target?: Vector3 | { x: number; y: number; z: number } | [number, number, number]
}

const DEFAULT_CAMERA_POSITION = toVector3([0, 1500, -2500])
const DEFAULT_CAMERA_TARGET = toVector3([0, 1500, 0])

function toVector3FromUnknown(value: CameraViewLike['position']): Vector3 | null {
  if (!value) return null
  if (value instanceof Vector3) return value.clone()
  if (Array.isArray(value)) return toVector3(value)
  if (typeof value === 'object' && 'x' in value && 'y' in value && 'z' in value) {
    return toVector3([value.x, value.y, value.z])
  }
  return null
}

export function normalizeCameraView(view?: CameraViewLike | null): CameraView | null {
  if (!view) return null

  const position = toVector3FromUnknown(view.position)
  const target = toVector3FromUnknown(view.target)
  if (!position || !target) return null

  return { position, target }
}

export function isValidCameraView(view?: CameraViewLike | null): view is CameraView {
  const normalized = normalizeCameraView(view)
  if (!normalized) return false

  const minX = -20000
  const maxX = 20000
  const minY = -20000
  const maxY = 20000
  const minZ = -20000
  const maxZ = 20000

  const isInRange = (vector: Vector3) =>
    vector.x >= minX &&
    vector.x <= maxX &&
    vector.y >= minY &&
    vector.y <= maxY &&
    vector.z >= minZ &&
    vector.z <= maxZ

  if (!isInRange(normalized.position) || !isInRange(normalized.target)) {
    return false
  }

  const emptyPosition = emptyLastView.position
  const emptyTarget = emptyLastView.target
  if (
    normalized.position.distanceTo(emptyPosition) < 1 &&
    normalized.target.distanceTo(emptyTarget) < 1
  ) {
    return false
  }

  if (
    normalized.position.distanceTo(DEFAULT_CAMERA_POSITION) < 1 &&
    normalized.target.distanceTo(DEFAULT_CAMERA_TARGET) > 1
  ) {
    return false
  }

  return true
}

export function serializeCameraViewForPersistence(
  view?: CameraViewLike | null,
): PersistedCameraView | null {
  const normalized = normalizeCameraView(view)
  if (!normalized || !isValidCameraView(normalized)) return null

  return {
    position: normalized.position.toArray() as [number, number, number],
    target: normalized.target.toArray() as [number, number, number],
  }
}

export function applyCameraViewToControls(
  controls: CameraControls,
  view: CameraView,
  animate = false,
) {
  if (!animate) {
    controls.stop()
  }
  controls.setTarget(view.target.x, view.target.y, view.target.z, animate)
  controls.setPosition(view.position.x, view.position.y, view.position.z, animate)
  controls.update()
}

export function captureCameraViewFromControls(
  controls: CameraControls,
  camera: { position: Vector3 },
): CameraView {
  return {
    position: camera.position.clone(),
    target: controls.getTarget(new Vector3()),
  }
}

const CAMERA_VIEW_DRIFT_EPSILON = 1

export function hasCameraViewDrift(
  current: CameraView,
  reference: CameraView,
  epsilonMm = CAMERA_VIEW_DRIFT_EPSILON,
): boolean {
  return (
    current.position.distanceTo(reference.position) > epsilonMm ||
    current.target.distanceTo(reference.target) > epsilonMm
  )
}

/**
 * Reaplica la vista tras N frames (por defecto 1).
 * Sustituye los timeouts múltiples que competían con la navegación del usuario.
 */
export function applyCameraViewAfterFrames(
  controls: CameraControls,
  view: CameraView,
  frameCount = 1,
): () => void {
  let cancelled = false
  let frameId = 0

  const step = (remaining: number) => {
    if (cancelled) return
    if (remaining <= 0) {
      applyCameraViewToControls(controls, view, false)
      return
    }
    frameId = window.requestAnimationFrame(() => step(remaining - 1))
  }

  frameId = window.requestAnimationFrame(() => step(frameCount - 1))

  return () => {
    cancelled = true
    window.cancelAnimationFrame(frameId)
  }
}

/** @deprecated Usar applyCameraViewAfterFrames */
export function scheduleApplyCameraViewToControls(
  controls: CameraControls,
  view: CameraView,
): () => void {
  return applyCameraViewAfterFrames(controls, view, 1)
}
